import { isForeignAuthorFeatureEnabled } from 'constant';
import { useGetCurrentUserQuery } from 'services';

import { useUserHasPermission } from './useUserHasPermission';

/**
 * @typedef {Object} ForeignAuthorProcess
 * @property {boolean} isForeignProcess Madde yabancı yazar sürecinde mi — koşullu görünürlük dallarının
 *   tek karar noktası.
 * @property {boolean} canActAsAuthor Current_user bu maddede yazar adına işlem yapabilir mi.
 */

/**
 * @description Yabancı yazar sürecinin "kim" katmanı. Yabancı yazarlı maddelerde yazara sistemden
 * bilgilendirme gitmediği için, normalde yazarın yaptığı işleri (sipariş onayı, madde yükleme, tercüme
 * gönderimi) maddenin **sorumlu ilim heyetinin başkanı veya sekreteri** yürütür.
 *
 * Süreç bilgisi `offer.hasForeignAuthor` alanından okunur; `authors[].isForeign` üzerinden türetilmez —
 * o alan tek bir yazarın yabancı olup olmadığını anlatır ve yalnız rozet/yazar formunda kullanılır.
 *
 * Hook yalnız **kim** sorusunu cevaplar; **nerede** sorusuna çağrı yerleri karar verir (statü 21 karar,
 * statü 22 gönderim, statü 54 tercüme). Bu yüzden içinde statü kontrolü yoktur.
 *
 * Admin/süper admin ayrıca ele alınmaz: `handlePermission` tanımı gereği admin'i de kapsar.
 *
 * UI görünürlüğü içindir; gerçek yetkiyi BE zorlar. Saf/yan etkisizdir; özellik bayrağı
 * (`REACT_APP_FEATURE_FOREIGN_AUTHOR`) kapalıyken ya da `offer`/current_user yokken her iki bayrak da
 * güvenli şekilde `false` döner.
 *
 * Kuralın tek kaynağı bu dosyadır; süreç akışı, BE kontratı ve açık sorular için bkz.
 * `docs/foreign-author-process.md`.
 *
 * @param {Object|null} offer Madde teklifi (`ArticleOffer`): `hasForeignAuthor` ve `ownerCommittee` alanları kullanılır.
 * @returns {ForeignAuthorProcess} Süreç ve yetki bayrakları.
 */
export const useForeignAuthorProcess = (offer) => {
  const { data: current_user } = useGetCurrentUserQuery();
  const { handlePermission } = useUserHasPermission();

  if (!isForeignAuthorFeatureEnabled || !offer || !current_user) {
    return { isForeignProcess: false, canActAsAuthor: false };
  }

  const isForeignProcess = Boolean(offer.hasForeignAuthor);

  return {
    isForeignProcess,
    canActAsAuthor: isForeignProcess && handlePermission(offer.ownerCommittee?.id),
  };
};
